import { useEffect, useState } from 'react';
import type { ChangeEvent, KeyboardEvent } from 'react';

const PERCENT = 100;
/** ↑ / ↓ change the zoom by this many percent. */
const STEP = 10;
/** How long (ms) the "outside the zoom range" message stays. */
const HINT_DURATION = 2500;

export type ZoomLevelInputProps = {
  zoom: number;
  minZoom: number;
  maxZoom: number;
  onZoom: (zoom: number) => void;
};

const toPercent = (zoom: number) => Math.round(zoom * PERCENT);

/** Typed text as a zoom level ("150", "150%", " 75 % " → 1.5, 1.5, 0.75), or null when it is not a number. */
export const parseZoomPercent = (text: string): number | null => {
  const value = Number.parseFloat(text.replace('%', '').trim());
  return Number.isFinite(value) ? value / PERCENT : null;
};

/** The zoom actually used for `level`, and the message to show when it had to be moved into the zoom range. */
export const fitZoomLevel = (level: number, minZoom: number, maxZoom: number): { zoom: number; hint: string | null } => {
  if (level < minZoom) return { zoom: minZoom, hint: `The smallest zoom is ${toPercent(minZoom)}%` };
  if (level > maxZoom) return { zoom: maxZoom, hint: `The largest zoom is ${toPercent(maxZoom)}%` };
  return { zoom: level, hint: null };
};

/** A change made by typing (an `InputEvent` with an input type), as opposed to ↑ / ↓ or the wheel. */
const isTyped = (event: ChangeEvent<HTMLInputElement>): boolean =>
  typeof (event.nativeEvent as Partial<InputEvent>).inputType === 'string';

/**
 * The zoom level as an editable percentage. A typed number zooms on Enter or when the field is left; Escape cancels;
 * ↑ / ↓ (and the mouse wheel while editing) zoom at once in steps of 10 %. A value outside the zoom range is moved to
 * the nearest limit and a short message says so. The field is as wide as its digits, so "86%" reads as one value.
 */
export const ZoomLevelInput = ({ zoom, minZoom, maxZoom, onZoom }: ZoomLevelInputProps) => {
  const [draft, setDraft] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const shown = draft ?? String(toPercent(zoom));

  useEffect(() => {
    if (!hint) return undefined;
    const timer = window.setTimeout(() => setHint(null), HINT_DURATION);
    return () => window.clearTimeout(timer);
  }, [hint]);

  const apply = (text: string) => {
    const level = parseZoomPercent(text);
    if (level !== null) {
      const fitted = fitZoomLevel(level, minZoom, maxZoom);
      if (fitted.hint) setHint(fitted.hint);
      onZoom(fitted.zoom);
    }
    setDraft(null);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isTyped(event)) setDraft(event.target.value);
    else apply(event.target.value);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      apply(event.currentTarget.value);
      event.currentTarget.blur();
    } else if (event.key === 'Escape') {
      setDraft(null);
      event.currentTarget.blur();
    }
  };

  return (
    <span className="cm-controls-zoomfield">
      <label className="cm-controls-zoomlevel nokey" title="Zoom level: type a percentage and press Enter">
        <input
          type="number"
          min={toPercent(minZoom)}
          max={toPercent(maxZoom)}
          step={STEP}
          aria-label="Zoom level in percent"
          value={shown}
          style={{ width: `${Math.max(shown.length, 1)}ch` }}
          onFocus={(event) => event.currentTarget.select()}
          onChange={handleChange}
          onBlur={(event) => {
            if (draft !== null) apply(event.currentTarget.value);
          }}
          onKeyDown={handleKeyDown}
        />
        <span aria-hidden="true">%</span>
      </label>
      <span className="cm-controls-zoomhint" role="status" aria-live="polite">
        {hint}
      </span>
    </span>
  );
};
